import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Period } from '@/lib/timePeriod';
import type { TopRulesResponse } from '@/types/fraudHub';

/**
 * Fetches the top-6 rules by period fire-count. Server-side ordering is
 * severity-first then count-desc — the SPA must NOT re-sort, so the
 * card grid renders in the order returned.
 *
 * Period-aware (PR 3): the "fires" total reflects the selected period;
 * the per-card sparkline stays at a fixed 7-day trailing window
 * (sparkline width is fixed in the SPA, so resampling per-period would
 * destroy the visual rhythm). `period_label` on the response drives the
 * panel header.
 */
export function useFraudHubTopRules(
  period: Period,
): UseQueryResult<TopRulesResponse, Error> {
  return useQuery<TopRulesResponse, Error>({
    queryKey: ['fraud-hub', 'top-rules', period],
    queryFn: async () => {
      const { data } = await api.get<TopRulesResponse>(
        '/fraud-hub/top-rules',
        { params: { period } },
      );
      return data;
    },
    staleTime: 30_000,
  });
}
